import { memo, useCallback, useRef } from 'react';
import type { VehicleType } from '@/types/survey';
import { formatNumber } from '@/utils/format';
import { VehicleIcon } from '@/components/VehicleIcon';

interface VehicleCounterCardProps {
  vehicleType: VehicleType;
  count: number;
  disabled: boolean;
  onIncrement: (vehicleTypeId: string) => void;
}

/**
 * Guard against the same physical tap arriving twice.
 *
 * Mobile browsers can fire a synthetic `click` after a `pointerup`, and a
 * fast double tap on a big button is almost always one vehicle, not two. 120 ms
 * is short enough that a genuinely fast surveyor (5+ vehicles/second) is not
 * blocked, but long enough to absorb duplicate events.
 */
const DUPLICATE_TAP_WINDOW_MS = 120;

function VehicleCounterCardComponent({
  vehicleType,
  count,
  disabled,
  onIncrement,
}: VehicleCounterCardProps) {
  const lastTapRef = useRef(0);

  const handleClick = useCallback(() => {
    if (disabled) return;
    const now = performance.now();
    if (now - lastTapRef.current < DUPLICATE_TAP_WINDOW_MS) return;
    lastTapRef.current = now;

    onIncrement(vehicleType.id);

    // Short haptic confirmation where supported (Android). iOS Safari ignores it.
    if (typeof navigator.vibrate === 'function') {
      navigator.vibrate(15);
    }
  }, [disabled, onIncrement, vehicleType.id]);

  const label = vehicleType.axleConfig
    ? `${vehicleType.name}, axles ${vehicleType.axleConfig}, count ${count}. Tap to add one.`
    : `${vehicleType.name}, count ${count}. Tap to add one.`;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      aria-label={label}
      title={vehicleType.description}
      className={[
        // h-full makes the card fill its grid cell, so the grid drives the size
        // rather than a fixed min-height forcing the page to scroll.
        'tap-target relative flex h-full min-h-0 flex-col items-center justify-center overflow-hidden rounded-xl border-2 px-1 py-1 transition-colors',
        disabled
          ? 'border-slate-200 bg-slate-50 text-slate-400'
          : 'border-slate-300 bg-white text-slate-900 active:border-brand-700 active:bg-brand-50',
      ].join(' ')}
    >
      <span className="flex w-full items-center justify-center gap-1 px-0.5">
        <VehicleIcon
          vehicleTypeId={vehicleType.id}
          className={`h-[clamp(0.6rem,2.1vh,1.15rem)] w-auto shrink-0 ${
            disabled ? 'text-slate-300' : 'text-slate-600'
          }`}
        />
        <span className="truncate text-[clamp(0.45rem,1.35vh,0.7rem)] font-bold uppercase leading-tight tracking-wide text-slate-500">
          {vehicleType.code}
        </span>
      </span>

      {/* The count stays the strongest element in the card at every size. */}
      <span className="font-mono text-[clamp(1rem,4vh,2.5rem)] font-bold leading-none tabular-nums">
        {formatNumber(count)}
      </span>

      {/* Axle notation is the only way to tell AG3–AG6 apart in the field. */}
      {vehicleType.axleConfig ? (
        <span className="w-full truncate text-center text-[clamp(0.4rem,1.15vh,0.6rem)] font-medium leading-tight text-slate-400">
          {vehicleType.axleConfig}
        </span>
      ) : (
        <span className="w-full truncate text-center text-[clamp(0.4rem,1.15vh,0.6rem)] leading-tight text-slate-400">
          {vehicleType.shortName}
        </span>
      )}
    </button>
  );
}

/**
 * Memoised on count/disabled: tapping one card must not re-render the other 14.
 * This is what keeps the grid responsive after thousands of taps.
 */
export const VehicleCounterCard = memo(
  VehicleCounterCardComponent,
  (prev, next) =>
    prev.count === next.count &&
    prev.disabled === next.disabled &&
    prev.vehicleType === next.vehicleType &&
    prev.onIncrement === next.onIncrement,
);
